// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/recorrido/guion.js
//  LO QUE SPACY LE CUENTA A QUIEN ACABA DE ENTRAR
//
//  Rob, 26-09-2026: «lo ideal es mandarlo a Spacy Coach, que detecte que es la
//  primera vez y ahí en el chat le explique al alumno qué es el coach, qué es
//  Spacy en lecciones, en situaciones, dashboard, teachers, el chat asistente,
//  mini games, el gym, entrevista, glosario, profesores, planes… vamos sacando
//  una a una con su botón de continuar».
//
//  ── POR QUÉ EL GUION VIVE AQUÍ Y NO REPARTIDO ──────────────────────────────
//  Porque es lo que se va a grabar. Cada paso acabará teniendo su audio con la
//  voz de Spacy —pregrabado, para no gastar Energía del alumno en algo que
//  siempre dice lo mismo—, y un texto repartido por once sitios es un texto que
//  nadie vuelve a leer entero antes de mandar a grabar.
//
//  ── LA REGLA DEL AUDIO, QUE ES LA QUE MÁS SE INCUMPLE ──────────────────────
//  Grabar CONGELA el texto: cambiarlo obliga a volver al estudio. Por eso aquí
//  NO se escribe nada que se mueva —precios, días de prueba, cantidad de
//  Energía Spacy—. Si la voz dice «350 tokens» y mañana son 500, el audio
//  empieza a mentir y nadie se entera, porque nadie vuelve a escucharlo.
//
//  Esos datos van en la pantalla, que sí se actualiza sola. El audio cuenta
//  PARA QUÉ sirve cada cosa, que es lo que no cambia.
//
//  ── `ruta` NO ES DECORATIVA ────────────────────────────────────────────────
//  Es la dirección que ese paso está explicando, y `audita_recorrido.mjs` la
//  usa para comprobar que sigue existiendo. El día que se retire una función,
//  la batería sale en rojo ANTES de que el recorrido se la siga anunciando a
//  un alumno durante meses. Es el mismo fallo del enlace de verificación que
//  apuntaba a `localhost`: nadie se entera hasta que alguien se queja.
//
//  `ruta: null` = no es una pantalla, es algo que vive dentro de otra (el chat
//  de ayuda es un botón de la cabecera, no una dirección).
// ═══════════════════════════════════════════════════════════════════════════════

// ⚠️  LOS AUDIOS NO SE RESUELVEN AQUÍ, sino en `audios.js`.
//
//  Este fichero es DATOS y tiene que poder leerse fuera del navegador: la
//  batería `audita_recorrido.mjs` lo importa desde Node para contrastar cada
//  `ruta` contra las de `App.jsx`, que es lo que impide que el recorrido siga
//  anunciando funciones retiradas.
//
//  El primer intento metió aquí `import.meta.glob`, que sólo existe dentro de
//  Vite. En el navegador funcionaba perfectamente; desde Node reventaba con
//  «glob is not a function» y la batería se quedaba sin poder leer el guion.
//  Es decir: la comprobación que existe para que nada envejezca en silencio
//  habría dejado de funcionar en silencio.

/**
 * Sube este número cuando cambie el ORDEN o se añada/quite un paso.
 * Un alumno a medio recorrido con el guion viejo retomaría en un paso que ya
 * no significa lo mismo; con la versión distinta, se le empieza de nuevo.
 */
export const VERSION_DEL_GUION = 1;

// ── NI UN «BIENVENIDO» EN TODO EL GUION ─────────────────────────────────────
//  Es la misma regla que hizo quitar lo de «fiera»: Rob, 29-09-2026, «no quiero
//  que ponga sobrenombres... porque si lo ocupa una mujer no está bien, incluso
//  un niño». Un «bienvenido» hace exactamente eso, y además en la primera
//  palabra que lee cualquiera que entre.
//
//  No se arregla con «bienvenid@» ni con «bienvenido/a»: eso se lee mal y se
//  oye peor, y esto está grabado. Se arregla no necesitando el adjetivo —«esto
//  es», «ésta es tu zona»—, que además va más al grano.
//
//  ⚠️  Cambiar cualquiera de estos textos obliga a regrabar SU audio con
//      `node graba_recorrido.mjs --grabar`. La huella de `audios/grabado.json`
//      se encarga de detectarlo, pero si no se graba, Spacy dirá una cosa y
//      sonará otra.
export const SALUDO = {
    titulo: 'Esto es',
    bloque: 'Non-Stop Learning',
    // Lo que se graba. Sin cifras ni promesas: esto no se toca en meses.
    texto:
        'Soy Spacy. Te mostraré cómo sacarle el máximo provecho a tu plataforma. '
        + 'Vamos a adentrarnos en un par de minutos en todo lo que te espera aquí dentro. '
        + '¿Comenzamos?',
    boton: 'Comencemos',
    omitir: 'Ya sé moverme',
};

/**
 * Los once pasos, en el orden en que se cuentan.
 *
 * El ORDEN no es decorativo: quien abandone a mitad —y alguno abandonará— se
 * lleva lo que haya visto hasta ahí. Por eso delante van las tres que hacen
 * que alguien vuelva mañana (entrenar, la lección, la conversación), y detrás
 * las que se buscan cuando ya hace falta (glosario, planes).
 */
/**
 * ⚠️  CADA PASO TIENE DOS NOMBRES, Y ES A PROPÓSITO.
 *
 *  `etiqueta` es la palabra suelta —Coach, Lab, Planes— y sólo se usa en el
 *  menú, donde hay once opciones juntas y lo que se busca es reconocerlas de
 *  un vistazo. `titulo` es el nombre completo de la pantalla, y es el que sale
 *  cuando Spacy explica ese paso.
 *
 *  Rob dio la lista exacta el 26-09-2026 («🏋️ Coach (Tu Coach Personal)…»), y
 *  es la que manda: son los nombres que el alumno va a ver luego en la app.
 *
 *  Ninguno de los dos se graba —el audio es `texto`—, así que cambiarlos no
 *  obliga a volver al estudio.
 */
export const PASOS = [
    {
        id: 'coach',
        etiqueta: 'Coach',
        icono: '🏋️',
        ruta: '/coach',
        titulo: 'Tu Coach Personal',
        texto:
            '¡Aquí es donde entro yo! Seré tu coach para guiar tu día a día. '
            + 'Puedes seleccionar entre entrenar por ejercicio, o lanzarte a hacer una rutina. '
            + 'Tú decides tu entrenamiento diario. Tu única misión es elegir, y arrancamos.',
    },
    {
        id: 'gimnasio',
        etiqueta: 'Gimnasio',
        icono: '⚡',
        ruta: '/entrenar',
        titulo: 'Tu Entrenamiento',
        texto:
            'Ésta es tu zona de entrenamiento. Aquí no hay rutinas obligatorias. '
            + 'Como en un gimnasio real, tú eliges la máquina y eliges el peso según lo que quieras fortalecer hoy. '
            + 'Puedes darle al botón de Comenzar entrenamiento para ir directo, o explorar cada área a tu propio ritmo.',
    },
    {
        id: 'lecciones',
        etiqueta: 'Clases',
        icono: '📚',
        ruta: '/home',
        titulo: 'Spacy Teacher',   // en la lista del menú Rob lo llamó así
        texto:
            'Aquí no vienes a escuchar clases, vienes a hablarlas. Tú tienes el control total: '
            + 'configura tu clase, define tu contexto y elige entre distintos modos de entrenar. '
            + 'Yo me adapto a todos los niveles para que practiques a tu ritmo, corrigiéndote al momento '
            + 'y, lo más importante, ¡sin que te dé vergüenza equivocarte!',
    },
    {
        id: 'tutor',
        etiqueta: 'Lab',
        icono: '🎭',
        ruta: '/tutor',
        titulo: 'Spacy Lab', // Actualizado para hacer match con la pantalla
        texto:
            'Esto es Spacy Lab. Aquí tú eliges tu propia aventura: desde tener una Conversación Libre, '
            + 'hasta simular que pides en un Restaurante VIP o hablas con Mentes Brillantes. '
            + 'Ajusta tu nivel de desafío, dale al botón de Despegar y dejemos que fluya. '
            + 'Es tu espacio seguro para practicar el momento antes del momento.',
    },
    {
        id: 'entrevista',
        etiqueta: 'Entrevistas',
        icono: '💼',
        ruta: '/entrevista',
        titulo: 'Interview Room', // Actualizado con el título de la UI
        texto:
            'Este es tu simulador. Puedes elegir un perfil ya listo, o pegar tu propio CV y la vacante real a la que aplicas. '
            + 'Yo seré el reclutador y te llevaré por las cinco fases de una entrevista completa en inglés. '
            + 'Solo dale al botón de Entrar a la entrevista. Vas a llegar a la de verdad habiéndolo hecho ya una vez.',
    },
    {
        id: 'juegos',
        etiqueta: 'Juegos',
        icono: '🎮',
        ruta: '/multigames',
        titulo: 'Mini Games',
        texto:
            'Para los días en que quieres algo distinto. Aquí tenemos juegos rápidos para ti: '
            + 'desde aprender con canciones y videos interesantes, hasta retos de verbos y mucho más. '
            + 'Elige uno arriba, juega un par de minutos, y la gramática dejará de sentirse como gramática.',
    },
    {
        id: 'glosario',
        etiqueta: 'Glosario',
        icono: '📖',
        ruta: '/glosario',
        titulo: 'Glosario y Fluency Nexus', // Actualizado para darle peso a la herramienta
        texto:
            'Olvídate de tomar apuntes, todo tu vocabulario se guarda y organiza solo. '
            + 'Pero esto no es una lista estática, aquí tienes el Fluency Nexus integrado para practicar. '
            + 'Lanza divertidos minijuegos para que entrenes lo que gustes: pon a prueba tu memoria muscular, tu pronunciación o tu velocidad. '
            + 'Entrena tu cerebro como un verdadero atleta.',
    },
    {
        id: 'progreso',   // el `id` interno se queda: es el nombre de su audio
        etiqueta: 'Dashboard',
        icono: '📊',
        ruta: '/dashboard',
        titulo: 'Tu Centro de Control', // Título actualizado a la magnitud de la pantalla
        texto:
            'Este es tu tablero principal. Aquí ves tus avances reales: tus puntos, tu racha y tu nivel. '
            + 'Pero también es tu centro de mando. En las pestañas de arriba puedes ajustar tus preferencias de aprendizaje, '
            + 'revisar cuántos tokens te quedan, o ver tu calendario de clases. '
            + 'Entra aquí para motivarte y comprobar que sí estás avanzando, incluso esos días en que sientas lo contrario.',
    },
    {
        id: 'profesores',
        etiqueta: 'Profesores',
        icono: '👨‍🏫',
        ruta: '/profesores',
        titulo: 'Elige tu Profesor', // Actualizado a la pantalla
        texto:
            'Si en algún momento quieres complementar tu entrenamiento, aquí tienes la opción de contratar a un teacher humano. '
            + 'Puedes agendar clases uno a uno, cien por ciento personalizadas. '
            + 'Toda clase que contrates en esta sección cuenta con la garantía total de NSL.',
    },
    {
        id: 'ayuda',
        etiqueta: 'Soporte',
        icono: '💬',
        // No es una pantalla: es el botón «Chat» de la barra de arriba.
        ruta: null,
        ancla: 'chat',
        titulo: 'Asistencia y Soporte',
        texto:
            'El botón de Chat de arriba abre a tu asistente Spacy para respuestas rápidas. '
            + 'Pero si tienes algún problema, o una duda con tu cuenta, por aquí mismo puedes comunicarte '
            + 'directamente con el equipo de asistencia NSL. Siempre habrá alguien listo para respaldarte.',
    },
    {
        id: 'plan',
        etiqueta: 'Planes',
        icono: '🎟️',
        ruta: '/plans',
        titulo: 'Tus Planes',
        texto:
            'Desde hoy ya arrancas con tu plan Freemium. Es tu pase de entrada para probar la plataforma '
            + 'y ver si nuestro ecosistema realmente se ajusta a tus objetivos. '
            + 'Cuando estés listo para llevar tu fluidez al siguiente nivel, aquí mismo tendrás los planes a tu elección.',
    },
];

/** El cierre. No es un paso más: es el que devuelve al alumno al producto. */
export const CIERRE = {
    titulo: 'Ya está. Ahora',
    bloque: 'a entrenar',
    texto:
        'Eso es todo lo que hay. No hace falta que te acuerdes de nada: cuando '
        + 'quieras repasarlo, lo tienes en «Cómo funciona». '
        + 'Y si tenemos nuevas actualizaciones, yo mismo como tu coach te las mostraré, '
        + 'o te avisaremos directo en el chat.',
    // ── EL FINAL SE QUEDA AQUI ──────────────────────────────────────────
    //  Rob, 30-09-2026: «el boton final debe abrir el coach, me esta
    //  redireccionando a /entrenar y quiero aqui mismo pero ya en el coach
    //  normal, porque ya termino su recorrido al 100%».
    //
    //  Tenia razon: el recorrido entero se lo acaba de contar Spacy en su
    //  chat, y el premio por terminarlo era que Spacy desapareciera y le
    //  dejara en una rejilla de salas. El ultimo paso ya explica el gimnasio;
    //  volver a mandarle alli es repetir lo que acaba de leer.
    //
    //  Sin `ruta` a proposito: no es que apunte a otro sitio, es que no apunta
    //  a ninguno. Cierra el recorrido y arranca la conversacion, que es donde
    //  estaba.
    boton: 'Empezar con mi coach',
};

/** El menú, para quien llega por el botón en vez de por el recorrido. */
export const MENU = {
    titulo: '¿Qué quieres',
    bloque: 'que te enseñe?',
    texto: 'Toca lo que te interese. No hace falta verlo en orden.',
};

export const TOTAL_PASOS = PASOS.length;
